import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import {
    Network, Zap, Activity, Plus, RefreshCcw, Copy, Sparkles, KeyRound, Trash2,
    PlugZap, Cpu, ShieldAlert, ExternalLink, Loader2, Settings2, CheckCircle2,
    XCircle, Clock, BarChart3, Webhook, ArrowLeftRight, ShieldCheck, ToggleLeft,
    ToggleRight, AlertTriangle, ChevronDown, ChevronUp, Eye, TestTube2, Repeat2,
    ScanSearch, PackagePlus, Puzzle, PlayCircle, CheckCheck, Layers, Timer,
    Radio, UploadCloud, Info, Stethoscope, ClipboardList, FlaskConical,
    UserCheck, UserX, Wifi, WifiOff, CircuitBoard, FileCode2, ClipboardCopy,
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { toast } from 'sonner';

const SEVERITY_COLORS = { low: '#10b981', medium: '#f59e0b', critical: '#dc2626' };
const PIE_COLORS = ['#10b981', '#f59e0b', '#dc2626', '#3b82f6', '#a855f7'];

const ALL_EVENTS = [
    'ad.created', 'ad.updated',
    'user.registered', 'user.updated',
    'wallet.topup', 'wallet.withdraw', 'wallet.transfer',
    'order.created', 'order.completed',
    'connect.test',
];

// ─── Status pills ─────────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
    const map = {
        connected: { label: 'Conectado', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
        connected_mock: { label: 'Conectado (mock)', className: 'bg-blue-100 text-blue-800 border-blue-300' },
        degraded: { label: 'Degradado', className: 'bg-amber-100 text-amber-800 border-amber-300' },
        disconnected: { label: 'Desconectado', className: 'bg-red-100 text-red-800 border-red-300' },
    };
    const s = map[status] || map.connected;
    return <Badge variant="outline" className={s.className}>{s.label}</Badge>;
};

const ReversePill = ({ status }) => {
    if (status === 'ok') return <Badge className="bg-emerald-600 text-white text-[10px]">↔ Inverso OK</Badge>;
    if (status === 'failed') return <Badge variant="destructive" className="text-[10px]">↔ Inverso fallido</Badge>;
    return <Badge variant="outline" className="text-[10px]">↔ Sin sincronizar</Badge>;
};

// ─── Circuit Breaker Banner ───────────────────────────────────────────────────

const CircuitBreakerBanner = ({ circuits }) => {
    if (!circuits || circuits.length === 0) return null;
    return (
        <Alert className="border-red-300 bg-red-50 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertTitle className="text-red-700">⚡ Circuit Breaker abierto</AlertTitle>
            <AlertDescription className="text-red-600 text-xs">
                {circuits.length} circuito(s) en estado OPEN (5+ fallos consecutivos → pausa de 15 min):
                {' '}{circuits.map(c => `${c.platform_id?.slice(0, 8)}…→${c.endpoint?.slice(0, 30)}`).join(' | ')}
            </AlertDescription>
        </Alert>
    );
};

// ─── 7-step Verification Dialog ───────────────────────────────────────────────

const VERIFY_STEPS_LABELS = [
    'Ping / Health',
    'Handshake',
    'Endpoints',
    'OAuth SSO',
    'Wallet Read',
    'Webhook Config',
    'Sincronización de datos',
];

const VerifyDialog = ({ open, onOpenChange, platform }) => {
    const [running, setRunning] = useState(false);
    const [steps, setSteps] = useState([]);
    const [allOk, setAllOk] = useState(null);

    const reset = () => { setSteps([]); setAllOk(null); };
    useEffect(() => { if (!open) reset(); }, [open]);

    const run = async () => {
        if (!platform?.id) return;
        setRunning(true);
        reset();
        // Animate step-by-step appearance as server streams (we poll result)
        try {
            const { data } = await api.post('/admin/connect/verify', { platform_id: platform.id });
            setSteps(data.steps || []);
            setAllOk(data.all_ok);
        } catch (e) {
            toast.error('Error al verificar: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setRunning(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-red-600" />
                        Verificación completa — {platform?.name}
                    </DialogTitle>
                    <DialogDescription>Suite de 7 pasos para validar la conexión bidireccional.</DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    {VERIFY_STEPS_LABELS.map((label, i) => {
                        const step = steps[i];
                        return (
                            <div key={i} className="flex items-center gap-3 rounded-lg border p-2.5">
                                <div className="w-5 shrink-0">
                                    {!step && running && i === steps.length
                                        ? <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                                        : !step
                                            ? <Clock className="h-4 w-4 text-muted-foreground" />
                                            : step.ok
                                                ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                : <XCircle className="h-4 w-4 text-red-500" />
                                    }
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium">{label}</div>
                                    {step && (
                                        <div className="text-xs text-muted-foreground">{step.detail}{step.latency_ms ? ` · ${step.latency_ms}ms` : ''}</div>
                                    )}
                                </div>
                                {step && (
                                    <Badge variant="outline" className={step.ok ? 'text-emerald-700 border-emerald-300' : 'text-red-700 border-red-300'}>
                                        {step.ok ? 'OK' : 'FAIL'}
                                    </Badge>
                                )}
                            </div>
                        );
                    })}
                </div>

                {allOk !== null && (
                    <div className={`rounded-lg p-3 text-center font-semibold ${allOk ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                        {allOk ? '✅ Todos los pasos pasaron' : `⚠️ ${steps.filter(s => !s.ok).length} paso(s) fallaron`}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
                    <Button onClick={run} disabled={running} className="bg-red-600 hover:bg-red-700">
                        {running ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Verificando…</> : <><TestTube2 className="h-4 w-4 mr-1" />Ejecutar verificación</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ─── Create Webhook Dialog ────────────────────────────────────────────────────

const CreateWebhookDialog = ({ open, onOpenChange, platforms, onDone }) => {
    const [form, setForm] = useState({ platform_id: '', target_url: '', events: [], description: '' });
    const [saving, setSaving] = useState(false);
    const [newSecret, setNewSecret] = useState(null);

    useEffect(() => { if (!open) { setForm({ platform_id: '', target_url: '', events: [], description: '' }); setNewSecret(null); } }, [open]);

    const toggleEvent = (ev) => {
        setForm(f => {
            const evs = f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev];
            return { ...f, events: evs };
        });
    };

    const save = async () => {
        if (!form.platform_id || !form.target_url || form.events.length === 0) {
            toast.error('Completa todos los campos y selecciona al menos un evento');
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post('/admin/connect/webhooks', form);
            setNewSecret(data.signing_secret);
            toast.success('Webhook creado');
            onDone && onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al crear webhook');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Webhook className="h-5 w-5 text-red-600" />Nuevo webhook</DialogTitle>
                </DialogHeader>
                {newSecret ? (
                    <div className="space-y-3">
                        <Alert className="border-emerald-300 bg-emerald-50">
                            <ShieldCheck className="h-4 w-4 text-emerald-600" />
                            <AlertTitle>Webhook creado — copia el secreto ahora</AlertTitle>
                            <AlertDescription className="text-xs">Este secreto sólo se muestra una vez. Úsalo para validar el header <code>X-OkConnect-Signature</code>.</AlertDescription>
                        </Alert>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-neutral-100 dark:bg-neutral-800 rounded p-2 break-all">{newSecret}</code>
                            <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard?.writeText(newSecret); toast.success('¡Copiado!'); }}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                        <DialogFooter><Button onClick={() => onOpenChange(false)}>Cerrar</Button></DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <Label>Plataforma *</Label>
                            <Select value={form.platform_id} onValueChange={v => setForm(f => ({ ...f, platform_id: v }))}>
                                <SelectTrigger><SelectValue placeholder="Selecciona plataforma" /></SelectTrigger>
                                <SelectContent>
                                    {(platforms || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>URL destino *</Label>
                            <Input value={form.target_url} onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))} placeholder="https://…/api/connect/webhook" />
                        </div>
                        <div>
                            <Label>Descripción</Label>
                            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Opcional" />
                        </div>
                        <div>
                            <Label>Eventos ({form.events.length} seleccionados) *</Label>
                            <div className="grid grid-cols-2 gap-1 mt-1 max-h-40 overflow-y-auto border rounded p-2">
                                {ALL_EVENTS.map(ev => (
                                    <label key={ev} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-secondary rounded p-1">
                                        <Checkbox checked={form.events.includes(ev)} onCheckedChange={() => toggleEvent(ev)} />
                                        <span className="font-mono">{ev}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                            <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                                {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Guardando…</> : 'Crear webhook'}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

// ─── Link Platform Modal (unchanged functionality, same as before) ─────────────

const LinkPlatformModal = ({ open, onOpenChange, onDone }) => {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({ name: '', url: '', platform_code: '', api_key: '' });
    const [probing, setProbing] = useState(false);
    const [probe, setProbe] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [includeSSO, setIncludeSSO] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [agentBusy, setAgentBusy] = useState(false);
    const [agentLog, setAgentLog] = useState([]);
    const [extraEndpoints, setExtraEndpoints] = useState([]);

    const reset = () => {
        setStep(1); setForm({ name: '', url: '', platform_code: '', api_key: '' });
        setProbe(null); setSelected(new Set()); setIncludeSSO(true);
        setAgentLog([]); setExtraEndpoints([]);
    };
    useEffect(() => { if (!open) reset(); }, [open]);

    const doProbe = async () => {
        if (!form.name || !form.url || !form.platform_code || !form.api_key) { toast.error('Completa todos los campos'); return; }
        if (!/^OK\d{4}[A-Z0-9]{7}$/.test(form.platform_code)) { toast.error('El código debe cumplir OK + DDMM + 7 alfanuméricos mayúsculas'); return; }
        setProbing(true);
        try {
            const { data } = await api.post('/admin/connect/platforms/probe', form);
            setProbe(data);
            setSelected(new Set((data.endpoints || []).map(e => `${e.method} ${e.path}`)));
            setStep(2);
            setAgentLog([{ at: new Date().toLocaleTimeString(), text: `Handshake completado. Origen: ${data.source}. Latencia ${data.latency_ms}ms. Endpoints: ${(data.endpoints || []).length}.` }]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error en handshake');
        } finally { setProbing(false); }
    };

    const runAgent = async (action, contextExtra = {}) => {
        setAgentBusy(true);
        setAgentLog(l => [...l, { at: new Date().toLocaleTimeString(), text: `Agente IA: ${action}…` }]);
        try {
            const { data } = await api.post('/admin/connect/agent/run', { action, tmp_id: probe?.tmp_id, context: { url: form.url, platform_code: form.platform_code, ...contextExtra } });
            if (action === 'discover') {
                setExtraEndpoints(data.extra_endpoints || []);
                setAgentLog(l => [...l, { at: new Date().toLocaleTimeString(), text: data.message || `${(data.extra_endpoints || []).length} endpoints extra descubiertos.` }]);
                const cur = new Set(selected);
                (data.extra_endpoints || []).forEach(e => cur.add(`${e.method} ${e.path}`));
                setSelected(cur);
            } else if (action === 'test_sso') {
                const rep = data.report;
                setAgentLog(l => [...l, { at: new Date().toLocaleTimeString(), text: `SSO → status: ${rep.status}. ${rep.recommendation || ''}` }]);
                (rep.checks || []).forEach(c => setAgentLog(l => [...l, { at: new Date().toLocaleTimeString(), text: `  ${c.ok ? '✅' : '❌'} ${c.name}: ${c.detail}` }]));
            } else if (action === 'generate_webhook') {
                const wh = data.webhook;
                setAgentLog(l => [...l, { at: new Date().toLocaleTimeString(), text: `Webhook: ${wh.webhook_url} · events: ${(wh.event_types || []).join(', ')}` }]);
            }
        } catch (e) {
            setAgentLog(l => [...l, { at: new Date().toLocaleTimeString(), text: 'Error: ' + (e?.response?.data?.detail || 'agente falló') }]);
        } finally { setAgentBusy(false); }
    };

    const confirmLink = async () => {
        if (selected.size === 0) { toast.error('Selecciona al menos un endpoint'); return; }
        setConfirming(true);
        try {
            await api.post('/admin/connect/platforms/confirm', { tmp_id: probe.tmp_id, selected_endpoints: Array.from(selected), include_oauth_sso: includeSSO });
            toast.success('Plataforma vinculada. El enlace inverso se registró automáticamente.');
            onDone && onDone(); onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al vincular');
        } finally { setConfirming(false); }
    };

    const allEps = useMemo(() => [...(probe?.endpoints || []), ...extraEndpoints], [probe, extraEndpoints]);
    const toggleAll = () => {
        const tags = allEps.map(e => `${e.method} ${e.path}`);
        setSelected(selected.size === tags.length ? new Set() : new Set(tags));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="link-platform-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><PlugZap className="h-5 w-5 text-red-600" />Vincular plataforma</DialogTitle>
                    <DialogDescription>Credenciales → handshake → selección de endpoints → confirmación + enlace inverso automático.</DialogDescription>
                </DialogHeader>

                {step === 1 && (
                    <div className="space-y-3">
                        <div><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Otra Plataforma S.A." data-testid="link-name" /></div>
                        <div><Label>URL *</Label><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://otraplataforma.com" data-testid="link-url" /></div>
                        <div><Label>Código ÚNICO * <span className="text-xs text-muted-foreground">(OK + DDMM + 7 alfanuméricos)</span></Label>
                            <Input value={form.platform_code} onChange={e => setForm({ ...form, platform_code: e.target.value.toUpperCase() })} placeholder="OK2501A9B3C2X" className="font-mono" maxLength={13} data-testid="link-code" /></div>
                        <div><Label>API Key *</Label><Input type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder="sk-…" data-testid="link-api-key" /></div>
                        <Alert><ShieldAlert className="h-4 w-4" /><AlertTitle>Cómo obtener estas credenciales</AlertTitle>
                            <AlertDescription className="text-xs">La plataforma remota debe tener el módulo <b>Super Admin Connect</b> instalado. El admin de esa plataforma te dará su código ÚNICO y API key.</AlertDescription>
                        </Alert>
                    </div>
                )}

                {step === 2 && probe && (
                    <div className="space-y-4">
                        <div className="rounded-xl border bg-secondary/40 p-3 flex items-center gap-2 text-sm">
                            <div className={`h-2 w-2 rounded-full ${probe.reachable ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                            <span className="font-medium">{probe.reachable ? 'Handshake completado' : 'Handshake mock'}</span>
                            <Badge variant="outline" className="ml-auto">Latencia: {probe.latency_ms}ms</Badge>
                        </div>
                        <div className="rounded-xl border p-3">
                            <div className="flex items-center gap-2 mb-2"><Cpu className="h-4 w-4 text-red-600" /><div className="font-semibold text-sm">Agente IA</div></div>
                            <div className="flex flex-wrap gap-2">
                                <Button size="sm" variant="outline" onClick={() => runAgent('discover')} disabled={agentBusy} data-testid="agent-discover"><Sparkles className="h-3.5 w-3.5 mr-1" />Descubrir endpoints</Button>
                                <Button size="sm" variant="outline" onClick={() => runAgent('test_sso')} disabled={agentBusy} data-testid="agent-test-sso"><KeyRound className="h-3.5 w-3.5 mr-1" />Probar OAuth SSO</Button>
                                <Button size="sm" variant="outline" onClick={() => runAgent('generate_webhook')} disabled={agentBusy} data-testid="agent-webhook"><Zap className="h-3.5 w-3.5 mr-1" />Generar webhook</Button>
                                {agentBusy && <Loader2 className="h-4 w-4 animate-spin text-red-600 self-center" />}
                            </div>
                            <div className="mt-2 max-h-36 overflow-y-auto text-xs bg-neutral-900 text-emerald-300 font-mono p-2 rounded" data-testid="agent-log">
                                {agentLog.length === 0 ? <div className="text-neutral-500">Log aparecerá aquí…</div>
                                    : agentLog.map((l, i) => <div key={i}><span className="text-neutral-500">[{l.at}]</span> {l.text}</div>)}
                            </div>
                        </div>
                        <div className="rounded-xl border p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-semibold text-sm">Endpoints ({selected.size}/{allEps.length})</div>
                                <Button size="sm" variant="ghost" onClick={toggleAll}>{selected.size === allEps.length ? 'Ninguno' : 'Todos'}</Button>
                            </div>
                            <div className="space-y-1 max-h-56 overflow-y-auto">
                                {allEps.map((e, i) => {
                                    const tag = `${e.method} ${e.path}`;
                                    const isExtra = extraEndpoints.some(x => `${x.method} ${x.path}` === tag);
                                    return (
                                        <label key={i} className="flex items-start gap-2 p-2 rounded hover:bg-secondary cursor-pointer" data-testid={`endpoint-${i}`}>
                                            <Checkbox checked={selected.has(tag)} onCheckedChange={v => { const s = new Set(selected); v ? s.add(tag) : s.delete(tag); setSelected(s); }} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <Badge variant="outline" className="font-mono text-[10px]">{e.method}</Badge>
                                                    <code className="text-xs">{e.path}</code>
                                                    {isExtra && <Badge className="bg-red-600 text-[10px]">IA</Badge>}
                                                </div>
                                                <div className="text-xs text-muted-foreground">{e.description}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="rounded-xl border p-3 flex items-start gap-3">
                            <Checkbox id="sso-chk" checked={includeSSO} onCheckedChange={setIncludeSSO} />
                            <div>
                                <label htmlFor="sso-chk" className="text-sm font-medium cursor-pointer">Incluir OAuth SSO</label>
                                <div className="text-xs text-muted-foreground">Permite que usuarios de esta plataforma inicien sesión con su cuenta vinculada.</div>
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {step === 1 && (<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                        <Button onClick={doProbe} disabled={probing} className="bg-red-600 hover:bg-red-700" data-testid="link-probe">
                            {probing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Probando…</> : 'Handshake →'}
                        </Button></>)}
                    {step === 2 && (<><Button variant="outline" onClick={() => setStep(1)}>← Atrás</Button>
                        <Button onClick={confirmLink} disabled={confirming} className="bg-red-600 hover:bg-red-700" data-testid="link-confirm">
                            {confirming ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Vinculando…</> : 'Vincular plataforma ✓'}
                        </Button></>)}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ─── Continuous Verification Tab ─────────────────────────────────────────────

const TYPE_META = {
    webhook_event: { label: 'Evento webhook', color: '#3b82f6', icon: '⚡' },
    endpoint:      { label: 'Endpoint',       color: '#10b981', icon: '🔗' },
    module:        { label: 'Módulo',          color: '#a855f7', icon: '🧩' },
    feature:       { label: 'Funcionalidad',   color: '#f59e0b', icon: '✨' },
};

const ACTION_META = {
    subscribed: { label: 'Suscrito',    cls: 'bg-blue-100 text-blue-800' },
    registered: { label: 'Registrado',  cls: 'bg-green-100 text-green-800' },
    integrated: { label: 'Integrado',   cls: 'bg-purple-100 text-purple-800' },
    noted:      { label: 'Registrado',  cls: 'bg-amber-100 text-amber-800' },
};

const ContinuousVerificationTab = ({ platforms }) => {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [scanningId, setScanningId] = useState(null);
    const [autoEnabled, setAutoEnabled] = useState(false);
    const [intervalMin, setIntervalMin] = useState(60);
    const [savingSettings, setSavingSettings] = useState(false);
    const [expanded, setExpanded] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/connect/continuous-verification');
            setState(data);
            setAutoEnabled(data.auto_verify_enabled || false);
            setIntervalMin(data.interval_minutes || 60);
        } catch { toast.error('Error al cargar verificación continua'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runAll = async () => {
        setScanning(true);
        try {
            const { data } = await api.post('/admin/connect/continuous-verification/run');
            toast.success(`Escaneo completo: ${data.summary?.new_capabilities || 0} nuevas capacidades integradas`);
            await load();
        } catch { toast.error('Error al ejecutar escaneo'); }
        finally { setScanning(false); }
    };

    const runOne = async (platformId) => {
        setScanningId(platformId);
        try {
            const { data } = await api.post(`/admin/connect/platforms/${platformId}/scan-capabilities`);
            const s = data.result;
            const total = Object.values(s.new_capabilities || {}).flat().length;
            toast.success(total > 0 ? `${total} nuevas capacidades integradas` : 'Sin cambios');
            await load();
        } catch { toast.error('Error al escanear plataforma'); }
        finally { setScanningId(null); }
    };

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            await api.put('/admin/connect/continuous-verification/settings', {
                auto_verify_enabled: autoEnabled,
                interval_minutes: parseInt(intervalMin, 10) || 60,
            });
            toast.success('Configuración guardada');
        } catch { toast.error('Error al guardar configuración'); }
        finally { setSavingSettings(false); }
    };

    const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    if (loading) return <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin" />Cargando…</div>;

    const lastRun = state?.last_run;
    const perPlatform = state?.per_platform || [];
    const integrations = state?.integrations || [];
    const modules = state?.modules || [];

    return (
        <div className="space-y-5">
            {/* ── Header row ── */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2">
                        <ScanSearch className="h-4 w-4 text-red-600" />
                        Verificación Continua de Capacidades
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Detecta nuevas funcionalidades en plataformas vinculadas y las integra automáticamente.
                    </div>
                </div>
                <Button onClick={runAll} disabled={scanning} className="bg-red-600 hover:bg-red-700 shrink-0">
                    {scanning
                        ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        : <PlayCircle className="h-4 w-4 mr-1" />}
                    {scanning ? 'Escaneando…' : 'Escanear todo'}
                </Button>
            </div>

            {/* ── Auto-verification settings ── */}
            <div className="rounded-2xl border bg-card p-4 flex flex-wrap items-center gap-4">
                <Timer className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Verificación automática</div>
                    <div className="text-xs text-muted-foreground">Escanea plataformas vinculadas de forma periódica en segundo plano.</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => setAutoEnabled(v => !v)} className="flex items-center gap-1.5 text-sm">
                        {autoEnabled
                            ? <ToggleRight className="h-6 w-6 text-emerald-600" />
                            : <ToggleLeft className="h-6 w-6 text-muted-foreground" />}
                        <span className={autoEnabled ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}>
                            {autoEnabled ? 'Activada' : 'Desactivada'}
                        </span>
                    </button>
                    {autoEnabled && (
                        <div className="flex items-center gap-1.5 text-sm">
                            <span className="text-muted-foreground">Intervalo:</span>
                            <input
                                type="number"
                                min={5} max={1440}
                                value={intervalMin}
                                onChange={e => setIntervalMin(e.target.value)}
                                className="w-20 border rounded px-2 py-1 text-sm text-center"
                            />
                            <span className="text-muted-foreground">min</span>
                        </div>
                    )}
                    <Button size="sm" variant="outline" onClick={saveSettings} disabled={savingSettings}>
                        {savingSettings ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Guardar'}
                    </Button>
                </div>
            </div>

            {/* ── Last run summary ── */}
            {lastRun && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                        { label: 'Plataformas escaneadas', value: lastRun.total, color: '' },
                        { label: 'Sin cambios',            value: lastRun.ok,    color: 'text-emerald-600' },
                        { label: 'Nuevas capacidades',     value: lastRun.new_capabilities, color: 'text-blue-600' },
                        { label: 'No alcanzables',         value: lastRun.unreachable, color: 'text-amber-600' },
                    ].map(({ label, value, color }) => (
                        <div key={label} className="rounded-xl border bg-card p-3">
                            <div className="text-[11px] text-muted-foreground">{label}</div>
                            <div className={`text-2xl font-bold mt-0.5 ${color}`}>{value ?? 0}</div>
                        </div>
                    ))}
                    <div className="col-span-2 sm:col-span-4 text-[11px] text-muted-foreground text-right">
                        Último escaneo completo: {new Date(lastRun.scanned_at).toLocaleString('es-MX')}
                    </div>
                </div>
            )}

            {/* ── Per-platform capability scan cards ── */}
            <div className="space-y-3">
                <div className="font-semibold text-sm flex items-center gap-2">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    Estado por plataforma
                </div>
                {perPlatform.length === 0 ? (
                    <div className="rounded-2xl border border-dashed p-8 text-center text-muted-foreground text-sm">
                        No hay plataformas vinculadas para verificar.
                    </div>
                ) : perPlatform.map(({ platform_id, platform_name, scan }) => {
                    const hasNew = scan?.status === 'new_capabilities';
                    const isExp = !!expanded[platform_id];
                    const newCaps = scan?.new_capabilities || {};
                    const totalNew = Object.values(newCaps).flat().length;

                    return (
                        <div key={platform_id} className="rounded-2xl border bg-card overflow-hidden">
                            <div className="p-4 flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium truncate">{platform_name}</span>
                                        {scan ? (
                                            scan.status === 'unreachable' ? (
                                                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">No alcanzable</Badge>
                                            ) : hasNew ? (
                                                <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px]">
                                                    +{totalNew} nuevas
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                                                    <CheckCheck className="h-3 w-3 mr-1" />Sin cambios
                                                </Badge>
                                            )
                                        ) : (
                                            <Badge variant="outline" className="text-[10px]">Sin escanear</Badge>
                                        )}
                                    </div>
                                    {scan?.scanned_at && (
                                        <div className="text-[11px] text-muted-foreground mt-0.5">
                                            Escaneado: {new Date(scan.scanned_at).toLocaleString('es-MX')}
                                        </div>
                                    )}
                                    {scan?.error && (
                                        <div className="text-[11px] text-amber-600 mt-0.5">{scan.error}</div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <Button
                                        size="sm" variant="outline"
                                        onClick={() => runOne(platform_id)}
                                        disabled={scanningId === platform_id}
                                    >
                                        {scanningId === platform_id
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            : <ScanSearch className="h-3.5 w-3.5 mr-1" />}
                                        {scanningId !== platform_id && 'Escanear'}
                                    </Button>
                                    {(hasNew || scan?.manifest) && (
                                        <Button size="sm" variant="ghost" onClick={() => toggle(platform_id)}>
                                            {isExp ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {isExp && (
                                <div className="border-t px-4 pb-4 pt-3 space-y-3">
                                    {/* New capabilities breakdown */}
                                    {hasNew && Object.entries(newCaps).map(([type, items]) =>
                                        items.length > 0 && (
                                            <div key={type}>
                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                                                    {TYPE_META[type]?.icon} {TYPE_META[type]?.label || type} ({items.length})
                                                </div>
                                                <div className="space-y-1">
                                                    {items.map((item, i) => (
                                                        <div key={i} className="flex items-start gap-2 rounded-lg bg-secondary/50 px-3 py-1.5">
                                                            <PackagePlus className="h-3.5 w-3.5 mt-0.5 shrink-0"
                                                                style={{ color: TYPE_META[type]?.color || '#888' }} />
                                                            <div className="text-xs">
                                                                <span className="font-mono font-medium">
                                                                    {item.event || item.path
                                                                        ? `${item.method ? item.method + ' ' : ''}${item.event || item.path || item.module_id || item.name || JSON.stringify(item)}`
                                                                        : item.name || JSON.stringify(item)}
                                                                </span>
                                                                {item.description && (
                                                                    <span className="text-muted-foreground ml-1">— {item.description}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {/* Remote manifest summary */}
                                    {scan?.manifest && (
                                        <div className="rounded-lg bg-secondary/30 p-3">
                                            <div className="text-[11px] font-semibold text-muted-foreground mb-2">MANIFIESTO REMOTO</div>
                                            <div className="flex flex-wrap gap-3 text-xs">
                                                {[
                                                    { label: 'Eventos', value: scan.manifest.events?.length || 0 },
                                                    { label: 'Endpoints', value: scan.manifest.endpoints?.length || 0 },
                                                    { label: 'Módulos', value: scan.manifest.modules?.length || 0 },
                                                    { label: 'Funcionalidades', value: scan.manifest.features?.length || 0 },
                                                ].map(({ label, value }) => (
                                                    <div key={label} className="text-center">
                                                        <div className="font-bold text-base">{value}</div>
                                                        <div className="text-muted-foreground">{label}</div>
                                                    </div>
                                                ))}
                                                <div className="ml-auto text-muted-foreground self-end">
                                                    v{scan.manifest.version || '?'} · {scan.manifest.protocol || '—'}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ── Integrated modules registry ── */}
            {modules.length > 0 && (
                <div className="rounded-2xl border bg-card p-4">
                    <div className="font-semibold text-sm flex items-center gap-2 mb-3">
                        <Puzzle className="h-4 w-4 text-red-600" />
                        Módulos integrados ({modules.length})
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {modules.map(m => (
                            <div key={m.id || m.module_id} className="rounded-xl border p-3">
                                <div className="flex items-center gap-2">
                                    <Puzzle className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                                    <span className="font-medium text-sm truncate">{m.name}</span>
                                    <Badge className="bg-purple-100 text-purple-800 text-[10px] ml-auto shrink-0">{m.status}</Badge>
                                </div>
                                {m.description && <div className="text-xs text-muted-foreground mt-1">{m.description}</div>}
                                <div className="flex gap-2 mt-2 flex-wrap">
                                    {(m.events || []).map(ev => (
                                        <span key={ev} className="text-[10px] rounded bg-blue-100 text-blue-800 px-1.5 py-0.5 font-mono">{ev}</span>
                                    ))}
                                </div>
                                <div className="text-[10px] text-muted-foreground mt-1">
                                    {platforms.find(p => p.id === m.platform_id)?.name || m.platform_id}
                                    {m.integrated_at && ` · ${new Date(m.integrated_at).toLocaleDateString('es-MX')}`}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Recent integrations log ── */}
            {integrations.length > 0 && (
                <div className="rounded-2xl border bg-card p-4">
                    <div className="font-semibold text-sm flex items-center gap-2 mb-3">
                        <Activity className="h-4 w-4 text-muted-foreground" />
                        Registro de integraciones automáticas
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {integrations.map(ig => {
                            const tm = TYPE_META[ig.type] || {};
                            const am = ACTION_META[ig.action] || { label: ig.action, cls: 'bg-gray-100 text-gray-700' };
                            const platName = platforms.find(p => p.id === ig.platform_id)?.name || ig.platform_id;
                            return (
                                <div key={ig.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-secondary/50 text-xs">
                                    <span style={{ color: tm.color || '#888' }} className="shrink-0">{tm.icon || '•'}</span>
                                    <span className="font-mono truncate flex-1">{ig.key}</span>
                                    <Badge className={`text-[9px] ${am.cls} shrink-0`}>{am.label}</Badge>
                                    <span className="text-muted-foreground shrink-0 text-[10px]">{platName}</span>
                                    <span className="text-muted-foreground shrink-0 text-[10px]">
                                        {new Date(ig.integrated_at).toLocaleString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!lastRun && !loading && integrations.length === 0 && (
                <div className="rounded-2xl border border-dashed p-10 text-center">
                    <ScanSearch className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <div className="font-semibold">Sin escaneos previos</div>
                    <div className="text-sm text-muted-foreground mt-1 mb-4">Ejecuta el primer escaneo para descubrir capacidades en tus plataformas vinculadas.</div>
                    <Button onClick={runAll} disabled={scanning} className="bg-red-600 hover:bg-red-700">
                        {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PlayCircle className="h-4 w-4 mr-1" />}
                        {scanning ? 'Escaneando…' : 'Escanear ahora'}
                    </Button>
                </div>
            )}
        </div>
    );
};

// ─── Endpoint Stats Tab ───────────────────────────────────────────────────────

const StatsTab = ({ platforms }) => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState([]);
    const [circuits, setCircuits] = useState([]);
    const [platformFilter, setPlatformFilter] = useState('all');
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = platformFilter !== 'all' ? `?platform_id=${platformFilter}` : '';
            const { data } = await api.get(`/admin/connect/endpoint-stats${params}`);
            setStats(data.stats || []);
            setCircuits(data.open_circuits || []);
        } catch (e) {
            toast.error('Error al cargar estadísticas');
        } finally { setLoading(false); }
    }, [platformFilter]);

    useEffect(() => { load(); }, [load]);

    const methodColor = { GET: '#3b82f6', POST: '#10b981', PUT: '#f59e0b', DELETE: '#dc2626', PATCH: '#a855f7' };

    return (
        <div className="space-y-4">
            <CircuitBreakerBanner circuits={circuits} />
            <div className="flex items-center gap-3 flex-wrap">
                <Select value={platformFilter} onValueChange={setPlatformFilter}>
                    <SelectTrigger className="w-48 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas las plataformas</SelectItem>
                        {(platforms || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />} Actualizar
                </Button>
                <div className="ml-auto text-xs text-muted-foreground">{stats.length} endpoints · últimos 7 días</div>
            </div>

            {loading ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Cargando…</div> : (
                <div className="space-y-2">
                    {stats.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin estadísticas registradas aún.</div>}
                    {stats.map((s, i) => (
                        <div key={i} className="rounded-2xl border bg-card">
                            <button
                                className="w-full flex items-center gap-3 p-3 text-left"
                                onClick={() => setExpanded(expanded === i ? null : i)}
                            >
                                <Badge style={{ backgroundColor: methodColor[s.method] || '#888', color: '#fff' }} className="text-[10px] font-mono w-12 justify-center shrink-0">
                                    {s.method}
                                </Badge>
                                <code className="text-sm flex-1 truncate">{s.endpoint}</code>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                                    <span className={s.success_rate >= 95 ? 'text-emerald-600 font-semibold' : s.success_rate >= 80 ? 'text-amber-600 font-semibold' : 'text-red-600 font-semibold'}>
                                        {s.success_rate}%
                                    </span>
                                    <span>{s.avg_latency_ms}ms</span>
                                    <span>{s.total_7d} req</span>
                                </div>
                                {expanded === i ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                            {expanded === i && (
                                <div className="border-t p-3">
                                    <div className="grid grid-cols-3 gap-3 mb-3 text-center">
                                        <div className="rounded border p-2"><div className="text-xs text-muted-foreground">Últimas 24h</div><div className="font-bold">{s.total_24h}</div></div>
                                        <div className="rounded border p-2"><div className="text-xs text-muted-foreground">7 días</div><div className="font-bold">{s.total_7d}</div></div>
                                        <div className="rounded border p-2"><div className="text-xs text-muted-foreground">Latencia prom</div><div className="font-bold">{s.avg_latency_ms}ms</div></div>
                                    </div>
                                    <div className="h-32">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={s.series_7d || []}>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v.slice(5)} />
                                                <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                                                <Tooltip />
                                                <Bar dataKey="ok" fill="#10b981" name="OK" stackId="a" />
                                                <Bar dataKey="fail" fill="#dc2626" name="Fail" stackId="a" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                    {(s.last_success || s.last_error) && (
                                        <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                                            {s.last_success && <div>✅ Último éxito: {new Date(s.last_success).toLocaleString('es-MX')}</div>}
                                            {s.last_error && <div>❌ Último error: {new Date(s.last_error).toLocaleString('es-MX')}{s.last_error_code ? ` (${s.last_error_code})` : ''}</div>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Webhooks Tab ─────────────────────────────────────────────────────────────

const WebhooksTab = ({ platforms }) => {
    const [loading, setLoading] = useState(false);
    const [webhooks, setWebhooks] = useState([]);
    const [platformFilter, setPlatformFilter] = useState('all');
    const [showCreate, setShowCreate] = useState(false);
    const [testing, setTesting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = platformFilter !== 'all' ? `?platform_id=${platformFilter}` : '';
            const { data } = await api.get(`/admin/connect/webhooks${params}`);
            setWebhooks(data || []);
        } catch (e) {
            toast.error('Error al cargar webhooks');
        } finally { setLoading(false); }
    }, [platformFilter]);

    useEffect(() => { load(); }, [load]);

    const deleteWebhook = async (id) => {
        try {
            await api.delete(`/admin/connect/webhooks/${id}`);
            toast.success('Webhook eliminado');
            load();
        } catch (e) {
            toast.error('Error al eliminar webhook');
        }
    };

    const testWebhook = async (id) => {
        setTesting(id);
        try {
            const { data } = await api.post(`/admin/connect/webhooks/${id}/test`);
            toast[data.ok ? 'success' : 'error'](data.ok
                ? `✅ Ping OK · ${data.latency_ms}ms`
                : `❌ Falló (HTTP ${data.status_code}) · ${data.latency_ms}ms`);
            load();
        } catch (e) {
            toast.error('Error al probar webhook');
        } finally { setTesting(null); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <Select value={platformFilter} onValueChange={setPlatformFilter}>
                    <SelectTrigger className="w-48 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas las plataformas</SelectItem>
                        {(platforms || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />} Actualizar
                </Button>
                <Button size="sm" onClick={() => setShowCreate(true)} className="ml-auto bg-red-600 hover:bg-red-700">
                    <Plus className="h-4 w-4 mr-1" />Nuevo webhook
                </Button>
            </div>

            {loading ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Cargando…</div> : (
                <div className="space-y-3">
                    {webhooks.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">No hay webhooks configurados. Crea uno con el botón de arriba.</div>}
                    {webhooks.map(wh => (
                        <div key={wh.id} className="rounded-2xl border bg-card p-4">
                            <div className="flex items-start gap-3 flex-wrap">
                                <Webhook className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge variant={wh.active ? 'default' : 'secondary'} className={wh.active ? 'bg-emerald-600' : ''}>{wh.active ? 'Activo' : 'Inactivo'}</Badge>
                                        <span className="text-xs font-medium">{wh.platform_name || '—'}</span>
                                        <span className="text-[10px] text-muted-foreground">{wh.description || ''}</span>
                                    </div>
                                    <code className="text-xs block mt-1 truncate text-muted-foreground">{wh.target_url}</code>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {(wh.events || []).map(ev => <Badge key={ev} variant="outline" className="text-[10px] font-mono">{ev}</Badge>)}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        {wh.last_fired_at ? `Último disparo: ${new Date(wh.last_fired_at).toLocaleString('es-MX')} · HTTP ${wh.last_status || '—'}` : 'Sin disparos aún'}
                                        {wh.consecutive_failures > 0 && <span className="text-amber-600 ml-1">· {wh.consecutive_failures} fallos consecutivos</span>}
                                    </div>
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                    <Button size="sm" variant="outline" disabled={testing === wh.id} onClick={() => testWebhook(wh.id)}>
                                        {testing === wh.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TestTube2 className="h-3.5 w-3.5 mr-1" />}Test
                                    </Button>
                                    <Button size="icon" variant="ghost" className="text-red-600 h-8 w-8" onClick={() => { if (window.confirm('¿Eliminar este webhook?')) deleteWebhook(wh.id); }}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <CreateWebhookDialog open={showCreate} onOpenChange={setShowCreate} platforms={platforms} onDone={load} />
        </div>
    );
};

// ─── Diagnostics Tab ──────────────────────────────────────────────────────────

const SEVERITY_STYLE = {
    critical: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-800 dark:text-red-200',
    high:     'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-950/30 dark:border-orange-800 dark:text-orange-200',
    medium:   'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950/30 dark:border-yellow-800 dark:text-yellow-200',
    low:      'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/30 dark:border-blue-800 dark:text-blue-200',
};
const SEVERITY_ICON = { critical: XCircle, high: AlertTriangle, medium: Info, low: CheckCircle2 };

const DiagnosticsTab = () => {
    const [running, setRunning] = useState(false);
    const [report, setReport] = useState(null);
    const [copied, setCopied] = useState(false);
    const [expandedGap, setExpandedGap] = useState(null);
    const [fixingWebhooks, setFixingWebhooks] = useState(false);

    const ensureUserEvents = async () => {
        setFixingWebhooks(true);
        try {
            const { data } = await api.post('/admin/connect/webhooks/ensure-user-events');
            toast.success(data.message);
            if (report) run(); // re-run diagnostics to reflect the fix
        } catch (e) {
            toast.error('Error al aplicar fix: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setFixingWebhooks(false);
        }
    };

    const run = async () => {
        setRunning(true);
        setReport(null);
        try {
            const { data } = await api.post('/admin/connect/diagnostics/full');
            setReport(data);
        } catch (e) {
            toast.error('Error al ejecutar diagnóstico: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setRunning(false);
        }
    };

    const copyInstructions = () => {
        if (!report?.llm_instructions) return;
        navigator.clipboard.writeText(report.llm_instructions).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        });
    };

    const crit = report?.summary?.issues_critical ?? 0;
    const high = report?.summary?.issues_high ?? 0;
    const total = report?.summary?.issues_total ?? 0;

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="rounded-2xl border bg-card p-5">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                        <Stethoscope className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                        <div className="font-semibold text-base">Agente de Diagnóstico Connect</div>
                        <div className="text-sm text-muted-foreground mt-0.5">
                            Analiza toda la infraestructura de Super Admin Connect: conectividad, cobertura de webhooks, sincronización de usuarios entre plataformas, circuit breakers y gaps de código. Genera instrucciones precisas para corregir cada problema.
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                    <Button onClick={run} disabled={running} className="bg-red-600 hover:bg-red-700 text-white">
                        {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Diagnosticando…</> : <><FlaskConical className="h-4 w-4 mr-2" />Ejecutar diagnóstico completo</>}
                    </Button>
                    {report && (
                        <span className="text-xs text-muted-foreground">
                            Última ejecución: {new Date(report.run_at).toLocaleString('es-MX')}
                        </span>
                    )}
                </div>
            </div>

            {/* Summary row */}
            {report && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                            { label: 'Plataformas', value: report.summary.total_platforms, icon: Network, color: 'text-blue-600' },
                            { label: 'Alcanzables', value: report.summary.reachable, icon: Wifi, color: 'text-green-600' },
                            { label: 'Críticos', value: crit, icon: XCircle, color: crit > 0 ? 'text-red-600' : 'text-muted-foreground' },
                            { label: 'Problemas', value: total, icon: AlertTriangle, color: total > 0 ? 'text-orange-500' : 'text-muted-foreground' },
                        ].map(({ label, value, icon: Icon, color }) => (
                            <div key={label} className="rounded-xl border bg-card p-3 flex items-center gap-2.5">
                                <Icon className={`h-5 w-5 ${color}`} />
                                <div>
                                    <div className="text-xl font-bold leading-none">{value}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Issues list */}
                    {report.issues?.length > 0 && (
                        <div className="rounded-2xl border bg-card p-4 space-y-2">
                            <div className="font-semibold text-sm mb-3 flex items-center gap-2">
                                <ClipboardList className="h-4 w-4" />Problemas detectados ({report.issues.length})
                            </div>
                            {report.issues.map((issue, i) => {
                                const Icon = SEVERITY_ICON[issue.severity] || Info;
                                return (
                                    <div key={i} className={`rounded-lg border px-3 py-2 flex items-start gap-2 text-sm ${SEVERITY_STYLE[issue.severity] || ''}`}>
                                        <Icon className="h-4 w-4 shrink-0 mt-0.5" />
                                        <div>
                                            <span className="font-semibold uppercase text-[11px] mr-2 opacity-70">{issue.severity}</span>
                                            {issue.platform && issue.platform !== 'GLOBAL' && (
                                                <span className="text-[11px] mr-2 opacity-70">[{issue.platform}]</span>
                                            )}
                                            <span className="font-mono text-[11px] mr-2 opacity-60">{issue.code}</span>
                                            <span>{issue.detail}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Code gaps */}
                    {report.code_gaps?.length > 0 && (
                        <div className="rounded-2xl border bg-card p-4 space-y-3">
                            <div className="font-semibold text-sm flex items-center gap-2">
                                <CircuitBoard className="h-4 w-4" />Gaps de código detectados
                            </div>
                            {report.code_gaps.map((gap, i) => (
                                <div key={gap.gap_id} className={`rounded-xl border ${gap.severity === 'critical' ? 'border-red-200 dark:border-red-800' : 'border-orange-200 dark:border-orange-800'}`}>
                                    <button
                                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                                        onClick={() => setExpandedGap(expandedGap === i ? null : i)}
                                    >
                                        <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${gap.severity === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'}`}>
                                            {gap.gap_id}
                                        </span>
                                        <span className="text-sm font-semibold flex-1">{gap.title}</span>
                                        {expandedGap === i ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                                    </button>
                                    {expandedGap === i && (
                                        <div className="px-3 pb-3 space-y-2 border-t pt-2">
                                            <p className="text-sm text-muted-foreground">{gap.description}</p>
                                            <div className="text-xs">
                                                <span className="font-semibold">Archivo:</span>{' '}
                                                <code className="bg-muted px-1 rounded">{gap.fix_file}</code>
                                            </div>
                                            <div className="text-xs">
                                                <span className="font-semibold">Ubicación:</span>{' '}
                                                <code className="bg-muted px-1 rounded text-[10px]">{gap.fix_function}</code>
                                            </div>
                                            <div className="text-xs font-semibold mt-1">Código a insertar:</div>
                                            <pre className="bg-neutral-900 text-emerald-300 text-[11px] font-mono p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">{gap.fix_snippet}</pre>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Per-platform connectivity table */}
                    {report.platforms?.length > 0 && (
                        <div className="rounded-2xl border bg-card p-4">
                            <div className="font-semibold text-sm flex items-center gap-2 mb-3">
                                <Network className="h-4 w-4" />Conectividad por plataforma
                            </div>
                            <div className="space-y-3">
                                {report.platforms.map(p => (
                                    <div key={p.id} className="rounded-xl border p-3 space-y-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {p.health_probe.ok
                                                ? <Wifi className="h-4 w-4 text-green-500" />
                                                : <WifiOff className="h-4 w-4 text-red-500" />}
                                            <span className="font-semibold text-sm">{p.name}</span>
                                            <Badge variant="outline" className="text-[10px]">{p.status_stored}</Badge>
                                            <span className="text-xs text-muted-foreground">{p.health_probe.latency_ms}ms</span>
                                            {p.open_circuits?.length > 0 && (
                                                <Badge className="bg-orange-500 text-[10px]">⚡ {p.open_circuits.length} circuit abierto</Badge>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 text-[11px]">
                                            <span className={`px-2 py-0.5 rounded-full ${p.has_inbound_secret ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {p.has_inbound_secret ? '✓' : '✗'} HMAC inbound
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full ${p.reverse_link_status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {p.reverse_link_status === 'ok' ? '✓' : '✗'} Reverse link
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full ${p.missing_user_events?.length === 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {p.missing_user_events?.length === 0 ? '✓' : '✗'} User sync events
                                            </span>
                                            {p.delivery_7d.success_rate_pct !== null && (
                                                <span className={`px-2 py-0.5 rounded-full ${p.delivery_7d.success_rate_pct >= 80 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                    {p.delivery_7d.success_rate_pct}% entrega 7d
                                                </span>
                                            )}
                                        </div>
                                        {p.missing_user_events?.length > 0 && (
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs text-red-600">
                                                    Eventos sin suscribir: {p.missing_user_events.join(', ')}
                                                </span>
                                                <Button size="sm" variant="outline" className="h-6 text-[11px] border-red-300 text-red-700 hover:bg-red-50" onClick={ensureUserEvents} disabled={fixingWebhooks}>
                                                    {fixingWebhooks ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <UserCheck className="h-3 w-3 mr-1" />}
                                                    Aplicar fix
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* LLM instructions block */}
                    {report.llm_instructions && (
                        <div className="rounded-2xl border bg-card p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <FileCode2 className="h-4 w-4 text-red-600" />
                                <span className="font-semibold text-sm">Instrucciones para el LLM</span>
                                <span className="text-xs text-muted-foreground ml-1">— copia y pégalas en el chat de Replit Agent</span>
                                <Button
                                    size="sm"
                                    variant={copied ? 'default' : 'outline'}
                                    className={`ml-auto ${copied ? 'bg-green-600 text-white' : ''}`}
                                    onClick={copyInstructions}
                                >
                                    {copied
                                        ? <><CheckCheck className="h-3.5 w-3.5 mr-1" />¡Copiado!</>
                                        : <><ClipboardCopy className="h-3.5 w-3.5 mr-1" />Copiar instrucciones</>}
                                </Button>
                            </div>
                            <pre className="bg-neutral-900 text-neutral-100 text-[11px] font-mono p-4 rounded-xl overflow-x-auto max-h-96 whitespace-pre-wrap">
                                {report.llm_instructions}
                            </pre>
                        </div>
                    )}

                    {total === 0 && (
                        <div className="rounded-2xl border border-green-200 bg-green-50 dark:bg-green-950/20 p-6 text-center">
                            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-2" />
                            <div className="font-semibold text-green-800 dark:text-green-300">¡Todo en orden!</div>
                            <div className="text-sm text-green-700 dark:text-green-400 mt-1">
                                No se detectaron problemas en la infraestructura de Super Admin Connect.
                            </div>
                        </div>
                    )}
                </>
            )}

            {!report && !running && (
                <div className="rounded-2xl border border-dashed p-10 text-center">
                    <Stethoscope className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <div className="font-semibold">Diagnóstico no ejecutado</div>
                    <div className="text-sm text-muted-foreground mt-1">
                        Haz clic en "Ejecutar diagnóstico completo" para analizar toda la infraestructura.
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Sync Tab ─────────────────────────────────────────────────────────────────

const SyncTab = ({ platforms, onRefresh }) => {
    const [togglingSSO, setTogglingSSO] = useState(null);
    const [syncingReverse, setSyncingReverse] = useState(null);
    const [walletStats, setWalletStats] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get('/admin/wallet/overview');
                setWalletStats(data);
            } catch { }
        })();
    }, []);

    const toggleSSO = async (platformId) => {
        setTogglingSSO(platformId);
        try {
            const { data } = await api.post(`/admin/connect/platforms/${platformId}/enable-sso`);
            toast.success(data.sso_enabled ? 'SSO habilitado' : 'SSO deshabilitado');
            onRefresh && onRefresh();
        } catch (e) {
            toast.error('Error al cambiar SSO: ' + (e?.response?.data?.detail || e.message));
        } finally { setTogglingSSO(null); }
    };

    const syncReverse = async (platformId) => {
        setSyncingReverse(platformId);
        try {
            const { data } = await api.post(`/admin/connect/platforms/${platformId}/sync-reverse`);
            toast[data.reverse_link_status === 'ok' ? 'success' : 'error'](
                data.reverse_link_status === 'ok' ? '↔ Enlace inverso sincronizado' : '↔ El enlace inverso falló — verifica URL y credenciales'
            );
            onRefresh && onRefresh();
        } catch (e) {
            toast.error('Error: ' + (e?.response?.data?.detail || e.message));
        } finally { setSyncingReverse(null); }
    };

    if (platforms.length === 0) {
        return <div className="text-sm text-muted-foreground py-8 text-center">Vincula al menos una plataforma para gestionar la sincronización.</div>;
    }

    return (
        <div className="space-y-4">
            {/* OkWallet global stats */}
            {walletStats && (
                <div className="rounded-2xl border bg-card p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <ArrowLeftRight className="h-5 w-5 text-red-600" />
                        <div className="font-semibold">Sincronización OkWallet</div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="rounded-xl border p-3 text-center"><div className="text-xs text-muted-foreground">Wallets totales</div><div className="text-xl font-bold mt-1">{walletStats.total_wallets || 0}</div></div>
                        <div className="rounded-xl border p-3 text-center"><div className="text-xs text-muted-foreground">Balance MXN total</div><div className="text-xl font-bold mt-1 text-emerald-600">${(walletStats.total_balance_mxn || 0).toFixed(2)}</div></div>
                        <div className="rounded-xl border p-3 text-center"><div className="text-xs text-muted-foreground">Puntos totales</div><div className="text-xl font-bold mt-1 text-amber-600">{(walletStats.total_points || 0).toFixed(0)}</div></div>
                        <div className="rounded-xl border p-3 text-center"><div className="text-xs text-muted-foreground">Wallets congeladas</div><div className="text-xl font-bold mt-1 text-red-600">{walletStats.frozen_wallets || 0}</div></div>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">Los eventos de topup, transferencia y retiro se despachan automáticamente vía webhook a todas las plataformas vinculadas.</div>
                </div>
            )}

            {/* Per-platform sync detail */}
            <div className="space-y-3">
                {platforms.map(p => {
                    const ssoStatus = p.oauth_sso_status || {};
                    const ssoEnabled = ssoStatus.enabled || false;
                    const reverseStatus = p.reverse_link_status;

                    return (
                        <div key={p.id} className="rounded-2xl border bg-card p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold">{p.name}</span>
                                        <StatusPill status={p.status} />
                                        <ReversePill status={reverseStatus} />
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">{p.url}</div>
                                    {p.reverse_link_at && (
                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                            Último enlace inverso: {new Date(p.reverse_link_at).toLocaleString('es-MX')}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Button size="sm" variant="outline" disabled={syncingReverse === p.id} onClick={() => syncReverse(p.id)}>
                                        {syncingReverse === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Repeat2 className="h-3.5 w-3.5 mr-1" />}Re-sincronizar
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-3 border-t pt-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-medium flex items-center gap-2">
                                            {ssoEnabled
                                                ? <ToggleRight className="h-4 w-4 text-emerald-500" />
                                                : <ToggleLeft className="h-4 w-4 text-muted-foreground" />}
                                            OAuth SSO Cross-Platform
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {ssoEnabled ? 'Habilitado — usuarios pueden iniciar sesión con cuenta vinculada' : 'Deshabilitado'}
                                        </div>
                                        {ssoStatus.auth_url && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{ssoStatus.auth_url}</div>}
                                    </div>
                                    <Button size="sm" variant="outline" disabled={togglingSSO === p.id} onClick={() => toggleSSO(p.id)}>
                                        {togglingSSO === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                        {ssoEnabled ? 'Deshabilitar SSO' : 'Habilitar SSO'}
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-3 border-t pt-3">
                                <div className="text-sm font-medium mb-2">Endpoints vinculados ({(p.endpoints || []).length})</div>
                                <div className="flex flex-wrap gap-1">
                                    {(p.endpoints || []).slice(0, 8).map((ep, i) => (
                                        <Badge key={i} variant="outline" className="text-[10px] font-mono">
                                            {ep.method} {ep.path}
                                        </Badge>
                                    ))}
                                    {(p.endpoints || []).length > 8 && <Badge variant="secondary" className="text-[10px]">+{(p.endpoints || []).length - 8} más</Badge>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─── Main AdminConnect Component ──────────────────────────────────────────────

export default function AdminConnect() {
    // ---- core state ----
    const [platformCode, setPlatformCode] = useState('');
    const [codeLoading, setCodeLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [platforms, setPlatforms] = useState([]);
    const [settings, setSettings] = useState(null);
    const [incidents, setIncidents] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showLink, setShowLink] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [srKey, setSrKey] = useState('');
    const [srModel, setSrModel] = useState('');
    const [savingSettings, setSavingSettings] = useState(false);
    const [severity, setSeverity] = useState('all');
    const [platformFilter, setPlatformFilter] = useState('all');
    const [diagnoseId, setDiagnoseId] = useState(null);
    const [diagnoseText, setDiagnoseText] = useState('');
    const [openCircuits, setOpenCircuits] = useState([]);

    // ---- verification ----
    const [verifyPlatform, setVerifyPlatform] = useState(null);
    const [showVerify, setShowVerify] = useState(false);

    const load = useCallback(async () => {
        try {
            const [pRes, sRes, iRes, mRes] = await Promise.all([
                api.get('/admin/connect/platforms'),
                api.get('/admin/connect/settings'),
                api.get('/admin/connect/incidents?days=30'),
                api.get('/admin/connect/metrics?days=30'),
            ]);
            setPlatforms(pRes.data || []);
            setSettings(sRes.data);
            setSrModel(sRes.data?.openrouter_model || 'openai/gpt-4o-mini');
            setIncidents(iRes.data || []);
            setMetrics(mRes.data);
        } catch (e) {
            toast.error('Error al cargar datos de Connect');
        } finally { setLoading(false); }
    }, []);

    const loadCode = useCallback(async () => {
        setCodeLoading(true);
        try {
            const { data } = await api.get('/admin/connect/platform-code');
            setPlatformCode(data.platform_code || '');
        } catch (e) {
            toast.error('Error al cargar código de plataforma');
        } finally { setCodeLoading(false); }
    }, []);

    const loadCircuits = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/connect/endpoint-stats');
            setOpenCircuits(data.open_circuits || []);
        } catch { }
    }, []);

    useEffect(() => { load(); loadCode(); loadCircuits(); }, []);

    const regenerateCode = async () => {
        setCodeLoading(true);
        try {
            const { data } = await api.post('/admin/connect/platform-code/regenerate');
            setPlatformCode(data.platform_code || '');
            toast.success('Código regenerado');
        } catch (e) {
            toast.error('Error al regenerar');
        } finally { setCodeLoading(false); }
    };

    const copyCode = () => {
        if (navigator.clipboard) navigator.clipboard.writeText(platformCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            await api.put('/admin/connect/settings', { openrouter_api_key: srKey || null, openrouter_model: srModel });
            toast.success('Ajustes guardados');
            setShowSettings(false);
            setSrKey('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setSavingSettings(false); }
    };

    const deletePlatform = async (id, name) => {
        if (!window.confirm(`¿Desvincular "${name}"?`)) return;
        try {
            await api.delete(`/admin/connect/platforms/${id}`);
            toast.success('Plataforma desvinculada');
            load();
        } catch (e) { toast.error('Error al desvincular'); }
    };

    const pingPlatform = async (id) => {
        try {
            const { data } = await api.post(`/admin/connect/platforms/${id}/ping`);
            const ok = data.status === 'connected';
            toast[ok ? 'success' : 'info'](
                ok
                    ? `✅ Conectado · ${data.latency_ms}ms`
                    : data.status === 'connected_mock'
                        ? `📡 Sin respuesta (mock) · ${data.latency_ms}ms — usa Re-probar para reintentar`
                        : `⚠️ Degradado · ${data.latency_ms}ms`
            );
            load();
        } catch (e) { toast.error('Error al hacer ping'); }
    };

    const reprobePlatform = async (id) => {
        try {
            const { data } = await api.post(`/admin/connect/platforms/${id}/reprobe`);
            if (data.ok) {
                toast.success(`✅ ${data.message}`);
            } else {
                toast.error(`❌ ${data.message || 'La plataforma sigue sin responder'}`);
            }
            load();
        } catch (e) { toast.error('Error al re-probar: ' + (e?.response?.data?.detail || e.message)); }
    };

    const runDiagnose = async (platformId) => {
        setDiagnoseId(platformId);
        setDiagnoseText('Analizando…');
        try {
            const { data } = await api.post('/admin/connect/agent/run', { action: 'diagnose', platform_id: platformId });
            setDiagnoseText(data.report?.text || JSON.stringify(data.report, null, 2));
        } catch (e) {
            setDiagnoseText('Error: ' + (e?.response?.data?.detail || e.message));
        }
    };

    const filteredIncidents = useMemo(() => incidents.filter(i => {
        if (severity !== 'all' && i.severity !== severity) return false;
        if (platformFilter !== 'all' && i.platform_id !== platformFilter) return false;
        return true;
    }), [incidents, severity, platformFilter]);

    const severityPie = useMemo(() => {
        const s = metrics?.incidents_by_severity || {};
        return Object.entries(s).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
    }, [metrics]);

    if (loading) return <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Cargando Super Admin Connect…</div>;

    return (
        <div className="space-y-6 pb-12">
            {/* Circuit breaker global alert */}
            <CircuitBreakerBanner circuits={openCircuits} />

            {/* Platform Code Card */}
            <div className="rounded-2xl border bg-card p-5" data-testid="platform-code-card">
                <div className="flex items-center gap-2 mb-3">
                    <Network className="h-5 w-5 text-red-600" />
                    <div className="font-semibold">Tu código de plataforma</div>
                    <Badge variant="outline" className="ml-auto text-xs">Super Admin Connect v2</Badge>
                </div>
                <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-lg tracking-widest rounded-lg bg-secondary px-4 py-2 select-all" data-testid="platform-code">{codeLoading ? '…' : platformCode}</code>
                    <Button size="icon" variant="ghost" onClick={copyCode} data-testid="copy-code"><Copy className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={regenerateCode} disabled={codeLoading} data-testid="regenerate-code">
                        {codeLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}{!codeLoading && 'Regenerar'}
                    </Button>
                </div>
                {copied && <div className="text-xs text-emerald-600 mt-1">¡Copiado!</div>}
                <div className="text-xs text-muted-foreground mt-2">Comparte este código con la otra plataforma para que puedan vincularse contigo. Formato: OK + DDMM + 7 alfanuméricos.</div>
            </div>

            {/* Actions row */}
            <div className="flex flex-wrap gap-2">
                <Button onClick={() => setShowLink(true)} className="bg-red-600 hover:bg-red-700" data-testid="link-platform-btn">
                    <Plus className="h-4 w-4 mr-1" />Vincular plataforma
                </Button>
                <Button variant="outline" onClick={load}><RefreshCcw className="h-4 w-4 mr-1" />Actualizar</Button>
                <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="settings-btn"><Settings2 className="h-4 w-4 mr-1" />Ajustes IA</Button>
            </div>

            <Tabs defaultValue="platforms">
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="platforms" data-testid="tab-platforms"><Network className="h-3.5 w-3.5 mr-1" />Plataformas ({platforms.length})</TabsTrigger>
                    <TabsTrigger value="sync" data-testid="tab-sync"><ArrowLeftRight className="h-3.5 w-3.5 mr-1" />Sincronización</TabsTrigger>
                    <TabsTrigger value="verification" data-testid="tab-verification"><ScanSearch className="h-3.5 w-3.5 mr-1" />Verificación</TabsTrigger>
                    <TabsTrigger value="webhooks" data-testid="tab-webhooks"><Webhook className="h-3.5 w-3.5 mr-1" />Webhooks</TabsTrigger>
                    <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 className="h-3.5 w-3.5 mr-1" />Estadísticas</TabsTrigger>
                    <TabsTrigger value="agent" data-testid="tab-agent"><Cpu className="h-3.5 w-3.5 mr-1" />Agente IA</TabsTrigger>
                    <TabsTrigger value="diagnostics" data-testid="tab-diagnostics"><Stethoscope className="h-3.5 w-3.5 mr-1" />Diagnóstico</TabsTrigger>
                    <TabsTrigger value="status" data-testid="tab-status"><Activity className="h-3.5 w-3.5 mr-1" />Estado</TabsTrigger>
                </TabsList>

                {/* ─── Platforms Tab ─── */}
                <TabsContent value="platforms" className="mt-4">
                    {platforms.length === 0 ? (
                        <div className="rounded-2xl border border-dashed p-10 text-center">
                            <PlugZap className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                            <div className="font-semibold">Sin plataformas vinculadas</div>
                            <div className="text-sm text-muted-foreground mt-1">Usa el botón "Vincular plataforma" para conectar con otra.</div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {platforms.map(p => (
                                <div key={p.id} className={`rounded-2xl border bg-card p-4 flex flex-col gap-3 ${p.status === 'connected_mock' ? 'border-blue-200 dark:border-blue-900' : ''}`} data-testid={`platform-card-${p.id}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold truncate">{p.name}</span>
                                                <StatusPill status={p.status} />
                                                <ReversePill status={p.reverse_link_status} />
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1 truncate">{p.url}</div>
                                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                                <code className="font-mono">{p.platform_code}</code>
                                                <span>·</span>
                                                <span>{(p.endpoints || []).length} endpoints</span>
                                                <span>·</span>
                                                <span>Uptime {p.uptime_pct}%</span>
                                            </div>
                                            {p.oauth_sso_status?.enabled && (
                                                <Badge className="bg-blue-600 text-[10px] mt-1">SSO activo</Badge>
                                            )}
                                        </div>
                                    </div>

                                    {/* Mock status explanation */}
                                    {p.status === 'connected_mock' && (
                                        <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 px-3 py-2 text-xs text-blue-800 dark:text-blue-200 flex items-start gap-2">
                                            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                            <div>
                                                <span className="font-semibold">Conexión mock activa</span> — la plataforma remota no respondió al handshake al momento de la vinculación. Los webhooks y la sincronización funcionan normalmente. Usa <b>Re-probar</b> cuando la plataforma remota esté en línea para establecer la conexión real.
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-1.5">
                                        <Button size="sm" variant="outline" onClick={() => pingPlatform(p.id)} data-testid={`ping-${p.id}`}>
                                            <Zap className="h-3.5 w-3.5 mr-1" />Ping
                                        </Button>
                                        {p.status === 'connected_mock' && (
                                            <Button size="sm" variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:border-blue-700" onClick={() => reprobePlatform(p.id)} data-testid={`reprobe-${p.id}`}>
                                                <Radio className="h-3.5 w-3.5 mr-1" />Re-probar
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => { setVerifyPlatform(p); setShowVerify(true); }} data-testid={`verify-${p.id}`}>
                                            <ShieldCheck className="h-3.5 w-3.5 mr-1" />Verificar
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => window.open(p.url, '_blank')}>
                                            <ExternalLink className="h-3.5 w-3.5 mr-1" />Abrir
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => deletePlatform(p.id, p.name)} data-testid={`delete-${p.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                        Conectado: {p.connected_at ? new Date(p.connected_at).toLocaleDateString('es-MX') : '—'}
                                        {p.last_ping && ` · Último ping: ${new Date(p.last_ping).toLocaleString('es-MX')}`}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* ─── Sync Tab ─── */}
                <TabsContent value="sync" className="mt-4">
                    <SyncTab platforms={platforms} onRefresh={load} />
                </TabsContent>

                {/* ─── Continuous Verification Tab ─── */}
                <TabsContent value="verification" className="mt-4">
                    <ContinuousVerificationTab platforms={platforms} />
                </TabsContent>

                {/* ─── Webhooks Tab ─── */}
                <TabsContent value="webhooks" className="mt-4">
                    <WebhooksTab platforms={platforms} />
                </TabsContent>

                {/* ─── Stats Tab ─── */}
                <TabsContent value="stats" className="mt-4">
                    <StatsTab platforms={platforms} />
                </TabsContent>

                {/* ─── Agent Tab ─── */}
                <TabsContent value="agent" className="mt-4 space-y-4">
                    <div className="rounded-2xl border bg-card p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Cpu className="h-5 w-5 text-red-600" />
                            <div className="font-semibold">Agente IA de conexión</div>
                            {settings?.has_openrouter_key ? <Badge className="bg-emerald-600">Configurado</Badge> : <Badge variant="destructive">No configurado</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">
                            El agente IA descubre endpoints, prueba OAuth SSO, genera webhooks y diagnostica plataformas vinculadas usando <b>OpenRouter</b>. Modelo actual: <code className="font-mono">{settings?.openrouter_model}</code>.
                        </p>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div className="rounded-xl border p-3"><Sparkles className="h-4 w-4 text-red-600 mb-1" /><div className="font-semibold text-sm">Descubrir endpoints</div><div className="text-xs text-muted-foreground">Analiza y sugiere endpoints típicos de marketplace, SSO y webhooks.</div></div>
                            <div className="rounded-xl border p-3"><KeyRound className="h-4 w-4 text-red-600 mb-1" /><div className="font-semibold text-sm">Probar OAuth SSO</div><div className="text-xs text-muted-foreground">Simula el flujo de vinculación de cuentas y detecta errores.</div></div>
                            <div className="rounded-xl border p-3"><Zap className="h-4 w-4 text-red-600 mb-1" /><div className="font-semibold text-sm">Generar webhooks</div><div className="text-xs text-muted-foreground">Crea configuración con firma HMAC, tipos de evento y retry policy.</div></div>
                        </div>
                        <div className="mt-3"><Button onClick={() => setShowSettings(true)} variant="outline" size="sm"><Settings2 className="h-4 w-4 mr-1" />Configurar API Key</Button></div>
                    </div>
                    <div className="rounded-2xl border bg-card p-4">
                        <div className="font-semibold text-sm mb-2">Diagnóstico rápido por plataforma</div>
                        {platforms.length === 0 ? (
                            <div className="text-sm text-muted-foreground">Vincula al menos una plataforma para diagnosticar.</div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {platforms.map(p => (
                                    <Button key={p.id} size="sm" variant="outline" onClick={() => runDiagnose(p.id)} data-testid={`diagnose-${p.id}`}>
                                        <Sparkles className="h-3.5 w-3.5 mr-1" />{p.name}
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* ─── Diagnostics Tab ─── */}
                <TabsContent value="diagnostics" className="mt-4">
                    <DiagnosticsTab />
                </TabsContent>

                {/* ─── Status Tab ─── */}
                <TabsContent value="status" className="mt-4 space-y-4">
                    {!metrics ? <div className="text-sm text-muted-foreground">Cargando…</div> : (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <div className="rounded-2xl border bg-card p-3" data-testid="metric-total-platforms"><div className="text-xs text-muted-foreground">Plataformas</div><div className="text-xl font-bold mt-1">{metrics.total_platforms}</div></div>
                                <div className="rounded-2xl border bg-card p-3" data-testid="metric-total-incidents"><div className="text-xs text-muted-foreground">Incidencias (30d)</div><div className="text-xl font-bold mt-1">{metrics.total_incidents}</div></div>
                                <div className="rounded-2xl border bg-card p-3" data-testid="metric-critical"><div className="text-xs text-muted-foreground">Críticas</div><div className="text-xl font-bold mt-1 text-red-600">{metrics.incidents_by_severity?.critical || 0}</div></div>
                                <div className="rounded-2xl border bg-card p-3" data-testid="metric-medium"><div className="text-xs text-muted-foreground">Medias</div><div className="text-xl font-bold mt-1 text-amber-600">{metrics.incidents_by_severity?.medium || 0}</div></div>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="rounded-2xl border bg-card p-4" data-testid="chart-requests">
                                    <div className="font-semibold mb-2 text-sm">Requests exitosos vs fallidos (30d)</div>
                                    <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={metrics.series}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v.slice(5)} />
                                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                            <Tooltip /><Legend />
                                            <Bar dataKey="success" fill="#10b981" name="Exitosos" />
                                            <Bar dataKey="failed" fill="#dc2626" name="Fallidos" />
                                        </BarChart>
                                    </ResponsiveContainer></div>
                                </div>
                                <div className="rounded-2xl border bg-card p-4" data-testid="chart-latency">
                                    <div className="font-semibold mb-2 text-sm">Latencia promedio (ms)</div>
                                    <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={metrics.series}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v.slice(5)} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="avg_latency_ms" stroke="#dc2626" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer></div>
                                </div>
                                <div className="rounded-2xl border bg-card p-4" data-testid="chart-severity">
                                    <div className="font-semibold mb-2 text-sm">Incidencias por severidad</div>
                                    <div className="h-56">
                                        {severityPie.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin incidencias</div> :
                                            <ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={severityPie} dataKey="value" nameKey="name" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                                                {severityPie.map((e, i) => <Cell key={i} fill={SEVERITY_COLORS[e.name] || PIE_COLORS[i]} />)}
                                            </Pie><Tooltip /></PieChart></ResponsiveContainer>}
                                    </div>
                                </div>
                                <div className="rounded-2xl border bg-card p-4" data-testid="chart-uptime">
                                    <div className="font-semibold mb-2 text-sm">Uptime por plataforma</div>
                                    <div className="space-y-2 max-h-56 overflow-y-auto">
                                        {(metrics.per_platform || []).length === 0 ? (
                                            <div className="text-sm text-muted-foreground">Sin plataformas</div>
                                        ) : metrics.per_platform.map(p => (
                                            <div key={p.id} className="rounded border p-2">
                                                <div className="flex items-center justify-between text-sm"><span className="font-medium truncate">{p.name}</span><span className="font-bold">{p.uptime_pct}%</span></div>
                                                <div className="h-2 bg-neutral-200 rounded-full overflow-hidden mt-1"><div className="h-full bg-emerald-500" style={{ width: `${p.uptime_pct}%` }} /></div>
                                                <div className="text-[10px] text-muted-foreground mt-1">{p.requests_ok}/{p.requests_total} exitosos · {p.avg_latency_ms}ms prom</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-2xl border bg-card p-4" data-testid="incidents-list">
                                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                                    <div className="font-semibold text-sm">Incidencias registradas</div>
                                    <div className="flex gap-2 flex-wrap">
                                        <Select value={severity} onValueChange={setSeverity}>
                                            <SelectTrigger className="w-40 h-8" data-testid="filter-severity"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Todas las severidades</SelectItem>
                                                <SelectItem value="critical">Crítica</SelectItem>
                                                <SelectItem value="medium">Media</SelectItem>
                                                <SelectItem value="low">Baja</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Select value={platformFilter} onValueChange={setPlatformFilter}>
                                            <SelectTrigger className="w-40 h-8" data-testid="filter-platform"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Todas las plataformas</SelectItem>
                                                {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {filteredIncidents.length === 0 ? (
                                    <div className="text-sm text-muted-foreground py-6 text-center">No hay incidencias.</div>
                                ) : (
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {filteredIncidents.map(i => (
                                            <div key={i.id} className="rounded border p-2 flex items-start gap-2" data-testid={`incident-${i.id}`}>
                                                <div className="h-2 w-2 rounded-full mt-2 shrink-0" style={{ background: SEVERITY_COLORS[i.severity] || '#888' }} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Badge variant="outline" className="text-[10px]">{i.severity}</Badge>
                                                        <Badge variant="secondary" className="text-[10px]">{i.kind}</Badge>
                                                        <span className="text-xs text-muted-foreground">{i.platform_name || '—'}</span>
                                                        <span className="text-[10px] text-muted-foreground ml-auto">{new Date(i.created_at).toLocaleString('es-MX')}</span>
                                                    </div>
                                                    <div className="text-sm mt-1">{i.message}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </TabsContent>
            </Tabs>

            {/* ─── Modals ─── */}
            <LinkPlatformModal open={showLink} onOpenChange={setShowLink} onDone={load} />

            <VerifyDialog open={showVerify} onOpenChange={setShowVerify} platform={verifyPlatform} />

            <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md" data-testid="settings-dialog">
                    <DialogHeader>
                        <DialogTitle>Ajustes del Agente IA</DialogTitle>
                        <DialogDescription>Configura la API Key de OpenRouter y el modelo a usar.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>API Key OpenRouter {settings?.has_openrouter_key && '(dejar vacío para conservar)'}</Label>
                            <Input type="password" value={srKey} onChange={e => setSrKey(e.target.value)} placeholder="sk-or-v1-…" data-testid="settings-key" />
                            <div className="text-[11px] text-muted-foreground mt-1">Obtén una en <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" className="text-red-600 hover:underline">openrouter.ai/keys</a></div>
                        </div>
                        <div>
                            <Label>Modelo</Label>
                            <Select value={srModel} onValueChange={setSrModel}>
                                <SelectTrigger data-testid="settings-model"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {(settings?.models_suggested || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSettings(false)}>Cancelar</Button>
                        <Button onClick={saveSettings} disabled={savingSettings} className="bg-red-600 hover:bg-red-700" data-testid="save-settings">
                            {savingSettings ? 'Guardando…' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!diagnoseId} onOpenChange={() => setDiagnoseId(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-red-600" />Diagnóstico IA</DialogTitle></DialogHeader>
                    <Textarea value={diagnoseText} readOnly rows={12} className="text-sm" data-testid="diagnose-result" />
                    <DialogFooter><Button onClick={() => setDiagnoseId(null)}>Cerrar</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
